import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { InlineLoadingState } from '../components/LoadingSpinner';
import { AppSidebar } from '../components/AppSidebar';
import { AppTopBar } from '../components/AppTopBar';
import { AppToastViewport } from '../components/AppToastViewport';
import { useNotifications } from '../hooks/useNotifications';
import { useToasts } from '../hooks/useToasts';
import type { AppNotification } from '../types/notifications';
import { NotificationRow } from '../components/notifications/NotificationRow';

type View = 'inbox' | 'archive';

/** Tastenkürzel nicht abfangen, solange in ein Feld getippt wird. */
function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  return el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName);
}

export function NotificationsPage() {
  const navigate = useNavigate();
  const { instanceId } = useParams<{ instanceId: string }>();
  const [userId, setUserId] = React.useState<string | null>(null);
  const [view, setView] = React.useState<View>('inbox');
  const archivedView = view === 'archive';
  const { notifications, unreadCount, loading, markRead, markAllRead, archive, unarchive, archiveAll } =
    useNotifications(userId, instanceId ?? null, { limit: 100, archived: archivedView });
  const { toasts, pushToast, dismissToast } = useToasts();
  /** Per Tastatur oder Maus markierte Zeile; E archiviert sie. */
  const [activeIndex, setActiveIndex] = React.useState(0);

  React.useEffect(() => {
    fetch('/api/me', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => setUserId(data?.user?.id ?? null))
      .catch(() => setUserId(null));
  }, []);

  React.useEffect(() => {
    setActiveIndex(0);
  }, [view]);

  React.useEffect(() => {
    if (activeIndex > 0 && activeIndex >= notifications.length) setActiveIndex(Math.max(0, notifications.length - 1));
  }, [activeIndex, notifications.length]);

  const handleOpen = React.useCallback(
    async (n: AppNotification) => {
      if (!n.is_read) await markRead(n.id);
      if (n.link) navigate(n.link);
    },
    [markRead, navigate],
  );

  const handleArchive = React.useCallback(
    (n: AppNotification) => {
      void archive(n).then((ok) => {
        if (!ok) {
          pushToast('Benachrichtigung konnte nicht archiviert werden.', 'error');
          return;
        }
        pushToast('Archiviert', 'info', { label: 'Rückgängig', onClick: () => void unarchive(n) });
      });
    },
    [archive, unarchive, pushToast],
  );

  const handleRestore = React.useCallback(
    (n: AppNotification) => {
      void unarchive(n).then((ok) => {
        pushToast(ok ? 'Wiederhergestellt' : 'Benachrichtigung konnte nicht wiederhergestellt werden.', ok ? 'success' : 'error');
      });
    },
    [unarchive, pushToast],
  );

  React.useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey || isTypingTarget(event.target)) return;
      if (notifications.length === 0) return;
      const key = event.key.toLowerCase();
      if (key === 'arrowdown' || key === 'j') {
        event.preventDefault();
        setActiveIndex((i) => Math.min(notifications.length - 1, i + 1));
      } else if (key === 'arrowup' || key === 'k') {
        event.preventDefault();
        setActiveIndex((i) => Math.max(0, i - 1));
      } else if (key === 'enter') {
        const n = notifications[activeIndex];
        if (n) {
          event.preventDefault();
          void handleOpen(n);
        }
      } else if (key === 'e' && !archivedView) {
        const n = notifications[activeIndex];
        if (n) {
          event.preventDefault();
          handleArchive(n);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [notifications, activeIndex, archivedView, handleOpen, handleArchive]);

  const tabClass = (active: boolean) =>
    `rounded-md px-3 py-1.5 text-sm font-medium transition ${
      active ? 'bg-app-surface text-app-text shadow-sm ring-1 ring-app-border' : 'text-app-text-muted hover:bg-app-hover'
    }`;

  return (
    <div className="h-screen flex bg-app-bg font-sans overflow-hidden">
      <AppSidebar />
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden pb-16 md:pb-0">
        <AppTopBar title="Benachrichtigungen" />
        <div className="app-scrollbar flex-1 overflow-y-auto overscroll-contain p-4 md:p-6">
          <div className="max-w-2xl mx-auto bg-app-surface border border-app-border rounded-lg shadow-sm overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-app-border">
              <h1 className="text-lg font-semibold text-app-text">Alle Benachrichtigungen</h1>
              {!archivedView && notifications.length > 0 ? (
                <div className="flex items-center gap-3">
                  {unreadCount > 0 ? (
                    <button
                      type="button"
                      className="text-sm text-pipedrive-blue hover:underline"
                      onClick={() => void markAllRead()}
                    >
                      Alle als gelesen markieren
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="text-sm text-pipedrive-blue hover:underline"
                    onClick={() => {
                      void archiveAll().then((ok) => {
                        if (!ok) pushToast('Benachrichtigungen konnten nicht archiviert werden.', 'error');
                      });
                    }}
                  >
                    Alle archivieren
                  </button>
                </div>
              ) : null}
            </div>
            <div className="flex items-center justify-between gap-2 border-b border-app-border bg-app-surface-muted px-4 py-2">
              <div className="flex gap-1" role="tablist" aria-label="Ansicht">
                <button type="button" role="tab" aria-selected={!archivedView} className={tabClass(!archivedView)} onClick={() => setView('inbox')}>
                  Posteingang{unreadCount > 0 ? ` (${unreadCount})` : ''}
                </button>
                <button type="button" role="tab" aria-selected={archivedView} className={tabClass(archivedView)} onClick={() => setView('archive')}>
                  Archiv
                </button>
              </div>
              <p className="hidden text-xs text-app-text-subtle md:block">
                {archivedView
                  ? 'Archivierte Benachrichtigungen werden nach 28 Tagen gelöscht.'
                  : '↑/↓ auswählen · Enter öffnen · E archivieren'}
              </p>
            </div>
            {loading ? (
              <InlineLoadingState label="Laden …" className="px-4 py-8" />
            ) : notifications.length === 0 ? (
              <p className="px-4 py-8 text-sm text-app-text-subtle">
                {archivedView ? 'Keine archivierten Benachrichtigungen' : 'Keine Benachrichtigungen'}
              </p>
            ) : (
              <ul>
                {notifications.map((n, index) => (
                  <NotificationRow
                    key={n.id}
                    notification={n}
                    onOpen={(row) => void handleOpen(row)}
                    action={archivedView ? 'restore' : 'archive'}
                    onAction={archivedView ? handleRestore : handleArchive}
                    active={index === activeIndex}
                    onHover={() => setActiveIndex(index)}
                    className="px-4 py-3.5"
                  />
                ))}
              </ul>
            )}
          </div>
        </div>
      </main>
      <AppToastViewport toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}
